import type { VhAdminFeature } from '@/admin/admin.types';
import { FRONTEND_FEATURE_NAMES } from '@/permissions/frontend-features';

/**
 * Features the frontend switches on by itself, gating no backend permission. The people service
 * keeps no list of them: marking one beta registers it, unmarking forgets it and drops it from
 * every audience. Add a name to `FRONTEND_FEATURES` to make it manageable on the All Features screen.
 */
export const VH_FRONTEND_FEATURES: readonly string[] = FRONTEND_FEATURE_NAMES;

/**
 * Adds every predefined frontend feature the backend does not know yet — one not marked beta —
 * so it can be marked from the list. Backend features come first, frontend ones after, by name.
 */
export const withFrontendFeatures = (features: VhAdminFeature[]): VhAdminFeature[] => {
  const known = new Set(features.map((feature) => feature.name));
  const unmarked: VhAdminFeature[] = VH_FRONTEND_FEATURES.filter((name) => !known.has(name)).map(
    (name) => ({ name, type: 'frontend', beta: false }),
  );

  const backend = features.filter((feature) => feature.type === 'backend');
  const frontend = [...features.filter((feature) => feature.type === 'frontend'), ...unmarked].sort(
    (a, b) => a.name.localeCompare(b.name),
  );

  return [...backend, ...frontend];
};

/** An audience can only hold a frontend feature while it is beta, i.e. while it is registered. */
export const isTargetable = (feature: VhAdminFeature): boolean =>
  feature.type === 'backend' || feature.beta;
